import { lang } from "../../../../middleware/lang";

export interface TourStep {
  id: string;
  /** Route the user must be on for this step */
  route: string;
  /** CSS selector of the element to highlight. null = centered modal (no highlight) */
  target: string | null;
  title: string;
  content: string;
  /** Position of the tooltip relative to the target */
  placement: "top" | "bottom" | "left" | "right" | "center";
  /** If true, the user must perform an action (e.g. fill a form) before advancing */
  waitForAction?: boolean;
  /** Label override for the next button */
  nextLabel?: string;
  /** If set, clicking next navigates here instead of just advancing */
  navigateTo?: string;
}

export const TOUR_STEPS: TourStep[] = [
  // --- Step 0: Welcome ---
  {
    id: "welcome",
    route: "/inventory",
    target: null,
    title: lang("Bienvenido al Inventario", "Welcome to Inventory"),
    content: lang(
      "Este tour te guiará paso a paso para configurar tu inventario. Aprenderás a crear ingredientes, vincularlos a tus productos con recetas, y así el sistema descontará automáticamente cuando vendas. ¡Empecemos!",
      "This tour will guide you step by step to set up your inventory. You'll learn to create ingredients, link them to your menu products with recipes, and the system will automatically deduct stock when you sell. Let's go!"
    ),
    placement: "center",
    nextLabel: lang("Empezar", "Start"),
  },

  // --- Step 1: Ingredients tab ---
  {
    id: "ingredients-tab",
    route: "/inventory",
    target: "[data-tour='nav-ingredients']",
    title: lang("Pestaña de Ingredientes", "Ingredients Tab"),
    content: lang(
      "Aquí ves todos tus ingredientes (materias primas). Los ingredientes son la base de todo: carne, verduras, aceites, etc.",
      "Here you see all your ingredients (raw materials). Ingredients are the foundation of everything: meat, vegetables, oils, etc."
    ),
    placement: "bottom",
  },

  // --- Step 2: New ingredient button ---
  {
    id: "new-ingredient-btn",
    route: "/inventory",
    target: "[data-tour='new-ingredient-btn']",
    title: lang("Crear tu primer ingrediente", "Create your first ingredient"),
    content: lang(
      "Haz clic aquí para crear tu primer ingrediente. Empieza con uno de tus ingredientes principales, como 'Pechuga de pollo' o 'Carne molida'.",
      "Click here to create your first ingredient. Start with one of your main ingredients, like 'Chicken breast' or 'Ground beef'."
    ),
    placement: "left",
    nextLabel: lang("Crear Ingrediente", "Create Ingredient"),
    navigateTo: "/inventory/new",
  },

  // --- Step 3: Ingredient form - name ---
  {
    id: "ingredient-form-name",
    route: "/inventory/new",
    target: "[data-tour='ingredient-name']",
    title: lang("Nombre del ingrediente", "Ingredient name"),
    content: lang(
      "Escribe el nombre de tu ingrediente. Usa nombres claros que todo tu equipo pueda entender. Ejemplo: 'Pechuga de pollo', 'Aceite vegetal', 'Tortilla de harina'.",
      "Type your ingredient name. Use clear names your whole team can understand. Example: 'Chicken breast', 'Vegetable oil', 'Flour tortilla'."
    ),
    placement: "right",
    waitForAction: true,
  },

  // --- Step 4: Unit ---
  {
    id: "ingredient-form-unit",
    route: "/inventory/new",
    target: "[data-tour='ingredient-unit']",
    title: lang("Unidad de medida", "Unit of measure"),
    content: lang(
      "Selecciona cómo mides este ingrediente: kg para carnes, litros para líquidos, piezas para tortillas, etc. Esta unidad se usará en recetas y compras.",
      "Select how you measure this ingredient: kg for meats, liters for liquids, pieces for tortillas, etc. This unit will be used in recipes and purchases."
    ),
    placement: "right",
  },

  // --- Step 5: Cost ---
  {
    id: "ingredient-form-cost",
    route: "/inventory/new",
    target: "[data-tour='ingredient-cost']",
    title: lang("Costo por unidad", "Cost per unit"),
    content: lang(
      "El precio que pagas por cada unidad (por kg, por litro, por pieza). Esto permite calcular el costo de alimento de tus platillos.",
      "The price you pay per unit (per kg, per liter, per piece). This enables food cost calculations for your dishes."
    ),
    placement: "right",
  },

  // --- Step 6: Category ---
  {
    id: "ingredient-form-category",
    route: "/inventory/new",
    target: "[data-tour='ingredient-category']",
    title: lang("Categoría", "Category"),
    content: lang(
      "Organiza tus ingredientes en categorías (proteínas, lácteos, verduras, etc.) para encontrarlos más fácil y hacer conteos por categoría.",
      "Organize your ingredients into categories (proteins, dairy, produce, etc.) to find them easily and do counts by category."
    ),
    placement: "right",
  },

  // --- Step 7: Low stock threshold ---
  {
    id: "ingredient-form-threshold",
    route: "/inventory/new",
    target: "[data-tour='ingredient-threshold']",
    title: lang("Alerta de stock bajo", "Low stock alert"),
    content: lang(
      "Cuando tu stock baje de esta cantidad, el sistema te avisará. Así nunca te quedas sin ingredientes a media jornada.",
      "When your stock falls below this amount, the system will alert you. So you never run out mid-shift."
    ),
    placement: "right",
  },

  // --- Step 8: Save button ---
  {
    id: "ingredient-form-save",
    route: "/inventory/new",
    target: "[data-tour='ingredient-save']",
    title: lang("Guardar ingrediente", "Save ingredient"),
    content: lang(
      "Cuando tengas los datos listos, haz clic en Guardar. Después podrás agregar más ingredientes desde la lista.",
      "When you have the data ready, click Save. You can add more ingredients from the list afterwards."
    ),
    placement: "top",
    waitForAction: true,
    nextLabel: lang("Guardar y continuar", "Save & continue"),
  },

  // --- Step 9: Back to list - go to recipes ---
  {
    id: "go-to-recipes",
    route: "/inventory",
    target: "[data-tour='nav-recipes']",
    title: lang("Ahora: Crear Recetas", "Now: Create Recipes"),
    content: lang(
      "¡Excelente! Ya tienes ingredientes. Ahora necesitas crear recetas para vincular esos ingredientes a tus productos del menú. Haz clic en la pestaña Recetas.",
      "Excellent! You have ingredients now. Next you need to create recipes to link those ingredients to your menu products. Click the Recipes tab."
    ),
    placement: "bottom",
    nextLabel: lang("Ir a Recetas", "Go to Recipes"),
    navigateTo: "/inventory/recipes",
  },

  // --- Step 10: Recipes overview ---
  {
    id: "recipes-overview",
    route: "/inventory/recipes",
    target: "[data-tour='nav-recipes']",
    title: lang("Pestaña de Recetas", "Recipes Tab"),
    content: lang(
      "Las recetas conectan tus productos del menú con los ingredientes del inventario. Cuando se vende un producto, el sistema descuenta automáticamente los ingredientes de su receta. Si ves un aviso amarillo de 'productos sin receta', esos productos NO descontarán inventario.",
      "Recipes connect your menu products with inventory ingredients. When a product is sold, the system automatically deducts the ingredients from its recipe. If you see a yellow 'products without recipe' warning, those products will NOT deduct inventory."
    ),
    placement: "bottom",
  },

  // --- Step 11: New recipe button ---
  {
    id: "new-recipe-btn",
    route: "/inventory/recipes",
    target: "[data-tour='new-recipe-btn']",
    title: lang("Crear una receta", "Create a recipe"),
    content: lang(
      "Haz clic aquí para crear tu primera receta. Vincularás un producto del menú con los ingredientes que necesita.",
      "Click here to create your first recipe. You'll link a menu product with the ingredients it needs."
    ),
    placement: "left",
    nextLabel: lang("Crear Receta", "Create Recipe"),
    navigateTo: "/inventory/recipes/new",
  },

  // --- Step 12: Recipe form - product search ---
  {
    id: "recipe-product-search",
    route: "/inventory/recipes/new",
    target: "[data-tour='recipe-product']",
    title: lang("Seleccionar producto", "Select product"),
    content: lang(
      "Busca el producto del menú para el que quieres crear la receta. Escribe el nombre y selecciónalo de la lista.",
      "Search for the menu product you want to create a recipe for. Type the name and select it from the list."
    ),
    placement: "right",
    waitForAction: true,
  },

  // --- Step 13: Recipe form - add ingredients ---
  {
    id: "recipe-ingredients",
    route: "/inventory/recipes/new",
    target: "[data-tour='recipe-ingredients']",
    title: lang("Agregar ingredientes", "Add ingredients"),
    content: lang(
      "Agrega los ingredientes de esta receta. Para cada uno: busca el ingrediente, ingresa la cantidad por porción y selecciona la unidad. Marca como 'opcional' los que el cliente puede quitar (ej. lechuga).",
      "Add the recipe ingredients. For each one: search the ingredient, enter the quantity per serving, and select the unit. Mark as 'optional' those the customer can remove (e.g. lettuce)."
    ),
    placement: "top",
    waitForAction: true,
  },

  // --- Step 14: Recipe form - cost summary ---
  {
    id: "recipe-cost-summary",
    route: "/inventory/recipes/new",
    target: "[data-tour='recipe-cost-summary']",
    title: lang("Resumen de costo", "Cost summary"),
    content: lang(
      "Aquí ves en tiempo real cuánto cuestan los ingredientes vs el precio de venta. El margen ideal es arriba del 65% (costo de alimento menor al 35%).",
      "Here you see in real-time how much ingredients cost vs the sale price. The ideal margin is above 65% (food cost below 35%)."
    ),
    placement: "right",
  },

  // --- Step 15: Save recipe ---
  {
    id: "recipe-save",
    route: "/inventory/recipes/new",
    target: "[data-tour='recipe-save']",
    title: lang("Guardar receta", "Save recipe"),
    content: lang(
      "Cuando tengas todos los ingredientes, haz clic en Guardar. A partir de ahora, cada vez que vendas este producto, el inventario se descontará automáticamente.",
      "When you have all ingredients, click Save. From now on, every time you sell this product, inventory will be deducted automatically."
    ),
    placement: "top",
    waitForAction: true,
    nextLabel: lang("Guardar y continuar", "Save & continue"),
  },

  // --- Step 16: Tour complete ---
  {
    id: "tour-complete",
    route: "/inventory/recipes",
    target: null,
    title: lang("¡Listo! Inventario configurado", "Done! Inventory set up"),
    content: lang(
      "Ya tienes lo esencial: ingredientes y recetas. El inventario se descontará automáticamente con cada venta.\n\nPróximos pasos recomendados:\n• Agrega más ingredientes y recetas para todos tus productos\n• Haz un conteo inicial de inventario (pestaña Conteos)\n• Revisa el Costo de Alimentos para optimizar tus márgenes\n• Registra merma cuando se desperdicie producto\n\nPuedes reiniciar este tour en cualquier momento desde el botón en la barra de navegación.",
      "You have the essentials: ingredients and recipes. Inventory will be automatically deducted with each sale.\n\nRecommended next steps:\n• Add more ingredients and recipes for all your products\n• Do an initial inventory count (Counts tab)\n• Review Food Cost to optimize your margins\n• Log waste when product is lost\n\nYou can restart this tour anytime from the button in the navigation bar."
    ),
    placement: "center",
    nextLabel: lang("Finalizar", "Finish"),
  },
];
